// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import React, { useEffect, useState } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";

type ContentAlignment = 'left' | 'right' | 'center'

interface CountdownStyles {
  xs?: string,
  sm?: string,
  md?: string,
  lg?: string,
  xl?: string
}

export type CountdownTimerBlocksProps = {
  id?: string;
  /* --- DESCRIPTION: ID of the component
  */
  countdownEndTimeUTC: string;
  /* --- DESCRIPTION: Timer's ending datetime, formatted as "MM/DD/YYYY hh:mm:ss"
  */
  persistOnExpire?: boolean;
  /* --- DESCRIPTION: FLAG if timer displays after countdown finished
  */
  background?: string;
  /* --- DESCRIPTION: hex bg color of the timer. Default is "transparent linear-gradient(180deg, #102641 0%, #03080D 100%) 0% 0% no-repeat padding-box"
  */
  timeBackground?: string;
  /* --- DESCRIPTION: bg color for each of the 3 time blocks. Default is #FFFFFF.
  */
  gridAlignment?: ContentAlignment;
  /* --- DESCRIPTION: global alignment of all text content. Can be overriden specifically
   * for countdownHeader, countdownNumbers, and countdownLabels text. Default is "center"
  */
  gridGap?: string | number;
  /* --- DESCRIPTION: gap in px between countdown blocks. Default is "16px"
  */
  margins?: CountdownStyles;
  /* --- DESCRIPTION: margin outside component
  */
  paddings?: CountdownStyles;
  /* --- DESCRIPTION: padding inside component (i.e., surrounding header and blocks)
  */
  countdownHeader?: any;
  /* --- DESCRIPTION: Text styles for the countdown header
  */
  countdownNumbers?: any;
  /* --- DESCRIPTION: Text styles for the countdown numbers themselves
  */
  countdownLabels?: any;
  /* --- DESCRIPTION: Text styles for the countdown labels (days, hours, minutes)
  */
};

export function CountdownTimerBlocks(props: CountdownTimerBlocksProps) {
  try {
    // -- reference --
    // const minute_in_seconds = 60;
    // const hour_in_seconds = 3600;
    // const day_in_seconds = 86400;
    const updatedProps = { ...props };
    Object.keys(props)?.forEach(key => {
      if (['margins', 'paddings', 'countdownHeader', 'countdownNumbers', 'countdownLabels'].includes(key)) {
        if (Array.isArray(props?.[key]) && props?.[key].length > 0) {
          // eslint-disable-next-line prefer-destructuring
          updatedProps[key] = props[key][0];
        }
      }
    });
    // eslint-disable-next-line no-param-reassign
    props = { ...updatedProps };

    const now = new Date().getTime();
    const countdownTime = new Date(props?.countdownEndTimeUTC ?? '').getTime();
    const timeLeft = countdownTime - now;
    const daysLeft = Math.floor(timeLeft / (24 * 60 * 60 * 1000));
    const hoursLeft = Math.floor( (timeLeft % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000) );
    const minutesLeft = Math.floor((timeLeft % (60 * 60 * 1000)) / (60 * 1000));
    const [days, setDays]: any = useState(daysLeft);
    const [hours, setHours]: any = useState(hoursLeft);
    const [minutes, setMinutes]: any = useState(minutesLeft);

    const setTimerState = (days, hours, minutes) => {
      setDays(days < 10 ? `0${  days}` : days);
      setHours(hours < 10 ? `0${  hours}` : hours);
      setMinutes(minutes < 10 ? `0${  minutes}` : `${minutes}`);
    };

    // set timer state as finished or time left
    let interval;
    const startTimer = (_countdown: number) => {
      interval = setInterval(() => {
        const now = new Date().getTime();
        const countdownTime = new Date(props?.countdownEndTimeUTC ?? '').getTime();
        const timeLeft = countdownTime - now;
        const days = Math.floor(timeLeft / (24 * 60 * 60 * 1000));
        const hours = Math.floor(
          (timeLeft % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
        );
        const minutes = Math.ceil((timeLeft % (60 * 60 * 1000)) / (60 * 1000));

        if (timeLeft < 0) {
          clearInterval(interval);
          setTimerState(0, 0, 0);
        } else {
          setTimerState(days, hours, minutes);
        }
      }, 1000);
    };

    useEffect(() => {
      const date = new Date();
      startTimer(date.getTime());
      return () => {
        clearInterval(interval);
      };
    }, []);

    const progressBox = (type: any, ix: number) => {
      try{
        let content: any, contentText: any;

        switch (type) {
          case "days":
            content = days >= 0 ? days.toString() : "0";
            contentText = "Days";
            break;
          case "hours":
            content = hours >= 0 ? hours.toString() : "0";
            contentText = "Hours";
            break;
          case "minutes":
            content = minutes >= 0 ? minutes.toString() : "0";
            contentText = "Minutes";
            break;
          default:
            break;
        }

        return (
          <DtvBox
            key={ix}
            sx={{
              marginLeft: (ix > 0) ?
                "16px" :
                "0",
              "@media (max-width: 230px)": {
                margin: `${(ix > 0) ? "16px" : "0"} 0 0 0 !important`
              },
              width: {
                xs: "75px",
                md: "94px"
              },
              minWidth: {
                xs: "60px",
                md: "94px"
              },
              height: {
                xs: "70px",
                md: "92px"
              },
              background: props?.timeBackground || "#FFFFFF 0% 0% no-repeat padding-box",
              borderRadius: "8px"
            }}>
            <DtvBox
              sx={{
                display: "flex",
                flexDirection: "column",
                flexWrap: "nowrap",
                justifyContent: "space-around",
                height: '100%',
                padding: "12px 8px"
              }}>
              <DtvText
                content={content}
                variant={props?.countdownNumbers?.variant || "h2"}
                sx={{
                  textAlign: props?.countdownNumbers?.alignment || "center",
                  fontFamily: props?.countdownNumbers?.fontFamily || DtvTheme?.typography?.h2?.fontFamily,
                  fontSize: {
                    xs: "32px !important",
                    md: "44px !important"
                  },
                  color: props?.countdownNumbers?.color || colorPalette?.jet?.main,
                  whiteSpace: "nowrap"
                }}
              />
              <DtvText
                content={contentText}
                variant={props?.countdownLabels?.variant || "subhead2medium"}
                sx={{
                  textAlign: props?.countdownLabels?.alignment || "center",
                  fontFamily: props?.countdownLabels?.fontFamily || "roboto-medium",
                  fontSize: {
                    xs: "12px",
                    md: "20px"
                  },
                  color: props?.countdownLabels?.color || colorPalette?.jet?.main,
                  whiteSpace: "nowrap"
                }}
              />
            </DtvBox>
          </DtvBox>
        );
      } catch (error) {
        return <ErrorDisplay error={error} />;
      }
    };

    const fixMargins = margins => {
      const marginMap = margins.split("px");
      const l = parseInt(marginMap[1].trim());
      const r = parseInt(marginMap[3].trim());
      if (l !== r) {
        return ((l - r) / 2)
      }
      return 0
    }

    const times = ["days", "hours", "minutes"];

    return (
      (days > 0 || hours > 0 || minutes > 0 || props?.persistOnExpire) ? (
        <DtvBox
          id={props?.id || "dtv-countdown-timer-blocks"}
          sx={{
            display: (props?.persistOnExpire || (days > 0 || hours > 0 || minutes > 0)) ? 'flex' : 'none',
            justifyContent: "center",
            margin: 'auto',
            maxWidth: {
              xs: 'calc(100vw - 40px)', // minus
              sm: 'fit-content'
            }
          }}>
          <DtvGrid
            item
            lg={12}
            md={12}
            sm={12}
            xl={12}
            xs={12}
            sx={{
              background: props?.background || `transparent linear-gradient(180deg, ${colorPalette.jet.main} 0%, #03080D 100%) 0% 0% no-repeat padding-box`,
              borderRadius: "8px",
              ...(props?.persistOnExpire || (days > 0 || hours > 0 || minutes > 0)) && {
                display: 'flex',
                flexDirection: {
                  xs: 'column',
                  sm: 'row'
                },
                margin: props?.margins || {
                  xs: "16px 20px 16px 20px",
                  sm: "16px 16px 20px 0px",
                  md: "16px 16px 20px 0px"
                },
                padding: props?.paddings || {
                  xs: "16px 20px 16px 20px",
                  sm: "16px 16px 16px 16px",
                  md: "16px 16px 16px 16px"
                },
                textAlign: props?.gridAlignment,
                position: {
                  xs: 'relative',
                  sm: 'unset'
                },
                left: {
                  xs: fixMargins(props?.margins?.xs),
                  sm: '0',
                  md: fixMargins(props?.margins?.md)
                }
              }
            }}>
              {props?.countdownHeader?.content && (
                <DtvBox
                  sx={{
                    alignSelf: "center",
                    width: {
                      xs: '100%',
                      sm: 'initial'
                    },
                    display: 'flex',
                    justifyContent: {
                      xs: props?.countdownHeader?.alignment || "center",
                      sm: 'center'
                    }
                  }}>
                  <DtvText
                    component={props?.countdownHeader?.component || "div"}
                    content={props?.countdownHeader?.content || "Offer ends in"}
                    variant={props?.countdownHeader?.variant || "subhead2medium"}
                    sx={{
                      padding: 0,
                      fontFamily: props?.countdownHeader?.fontFamily || "roboto-medium",
                      fontSize: "20px",
                      color: props?.countdownHeader?.color || "#FFFFFF",
                      overflowWrap: "break-word",
                      mb: {
                        xs: '12px',
                        sm: '0'
                      },
                      mr: {
                        xs: "0",
                        sm: "12px"
                      },
                      minWidth: {
                        xs: "unset",
                        sm: "164px"
                      },
                      textAlign: {
                        xs: props?.countdownHeader?.alignment || "center",
                        sm: props?.countdownHeader?.alignment || "center"
                      }
                    }}
                  />
                </DtvBox>
              )}
              <DtvGrid
                container={true}
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  "@media (max-width: 230px)": {
                    flexWrap: "wrap",
                    flexDirection: "column",
                    alignItems: "center"
                  }
                }}>
                {times.map((item, ix) => progressBox(item, ix))}
              </DtvGrid>
          </DtvGrid>
        </DtvBox>
      ) : (
        <></>
      )
    );
  } catch (error) {
    return <ErrorDisplay component="DTV CountdownTimer" error={error} />;
  }
}
